import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };

function IconArrow({ className = 'w-4 h-4' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><path d="M5 12h14M12 5l7 7-7 7" /></svg>;
}
function IconMonitor({ className = 'w-8 h-8' }) {
  return <svg viewBox="0 0 24 24" className={className} {...S}><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>;
}
function IconCross({ className = 'w-8 h-8' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round">
      <path d="M12 3v18M4 8h16" />
    </svg>
  );
}

export default function Splitter() {
  const [activePanel, setActivePanel] = useState('sb');
  const sbRef = useRef(null);
  const mnRef = useRef(null);

  useEffect(() => {
    if (window.innerWidth >= 768) return;
    const ratios = { sb: 0, mn: 0 };
    const thresholds = Array.from({ length: 21 }, (_, i) => i / 20);

    const update = () => {
      setActivePanel(ratios.sb >= ratios.mn ? 'sb' : 'mn');
    };

    const sbObs = new IntersectionObserver(([e]) => { ratios.sb = e.intersectionRatio; update(); }, { threshold: thresholds });
    const mnObs = new IntersectionObserver(([e]) => { ratios.mn = e.intersectionRatio; update(); }, { threshold: thresholds });

    if (sbRef.current) sbObs.observe(sbRef.current);
    if (mnRef.current) mnObs.observe(mnRef.current);

    return () => { sbObs.disconnect(); mnObs.disconnect(); };
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col bg-slate-950 overflow-hidden selection:bg-emerald-200/40">
      {/* Subtle ambient glows */}
      <div className="pointer-events-none absolute -top-24 left-1/4 h-96 w-96 rounded-full blur-3xl bg-emerald-600/10" />
      <div className="pointer-events-none absolute -bottom-24 right-1/4 h-96 w-96 rounded-full blur-3xl bg-amber-500/8" />

      {/* Logo bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex justify-center py-7">
        <Link to="/" className="flex items-center gap-2.5">
          <svg viewBox="0 0 512 512" className="h-9 w-9 text-white"
            fill="none" stroke="currentColor" strokeWidth={38} strokeLinecap="round" strokeLinejoin="round">
            <g transform="translate(256,256)">
              <circle cx="0" cy="0" r="140" />
              <path d="M -50 105 L -50 -105" />
              <path d="M -50 -105 L 10 -105 A 60 60 0 0 1 10 15 L -50 15" />
            </g>
          </svg>
          <span className="text-xl font-semibold tracking-tight text-white">Peronyx</span>
        </Link>
      </div>

      {/* "Or" center badge — desktop only */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 hidden md:flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 border border-white/[0.10] shadow-lg shadow-black/40">
        <span className="text-[11px] font-semibold text-slate-500 tracking-wider">or</span>
      </div>

      {/* Split panels */}
      <div className="flex-1 flex flex-col md:flex-row min-h-screen">

        {/* ── Business panel ───────────────────────────────────────────── */}
        <Link to="/business" ref={sbRef}
          className="relative flex-1 flex items-center justify-center overflow-hidden group min-h-[50vh] md:min-h-screen cursor-pointer">
          <div className="absolute inset-0 bg-slate-950" />
          <img src="/buisnessBG.webp" alt="" role="presentation"
            className={`absolute inset-0 h-full w-full object-cover blur-[1px] transition-all duration-700 scale-105 ${activePanel === 'sb' ? 'opacity-35' : 'opacity-0 md:group-hover:opacity-35'}`} />
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/40 via-transparent to-transparent opacity-60" />
          <div className="absolute inset-0 bg-emerald-500/0 group-hover:bg-emerald-500/[0.04] transition-colors duration-500" />
          {/* Right divider */}
          <div className="absolute right-0 top-0 bottom-0 w-px bg-white/[0.06] hidden md:block" />

          <div className="relative z-10 flex flex-col items-center text-center px-8 pt-24 pb-10 md:py-24 max-w-sm md:max-w-md">
            <div className="mb-6 h-14 w-14 md:h-16 md:w-16 rounded-2xl bg-emerald-500/[0.09] border border-emerald-500/20 grid place-items-center text-emerald-400 group-hover:bg-emerald-500/[0.16] group-hover:border-emerald-500/35 group-hover:scale-110 transition-all duration-400">
              <IconMonitor />
            </div>
            <p className="text-[10px] font-bold tracking-[0.32em] text-emerald-400/60 uppercase mb-3">For Businesses</p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white leading-[1.08] mb-4 group-hover:-translate-y-1 transition-transform duration-400">
              Build My<br />Business Website
            </h2>
            <p className="text-slate-400 text-sm md:text-[15px] leading-relaxed mb-7">
              Professional websites for businesses. Custom designed,
              fast, and built to convert visitors into customers.
            </p>
            <span className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-6 py-3 text-sm font-semibold text-white group-hover:bg-emerald-600 group-hover:shadow-xl group-hover:shadow-emerald-950/60 transition-all duration-300">
              Get Started
              <span className="group-hover:translate-x-0.5 transition-transform duration-300 inline-flex">
                <IconArrow />
              </span>
            </span>
          </div>
        </Link>

        {/* ── Mobile "or" divider ───────────────────────────────────────── */}
        <div className="flex items-center gap-4 px-8 py-3 md:hidden z-10 relative">
          <div className="flex-1 h-px bg-white/[0.07]" />
          <span className="text-[11px] font-semibold text-slate-600 tracking-widest uppercase">or</span>
          <div className="flex-1 h-px bg-white/[0.07]" />
        </div>

        {/* ── Ministry panel ────────────────────────────────────────────── */}
        <Link to="/ministry" ref={mnRef}
          className="relative flex-1 flex items-center justify-center overflow-hidden group min-h-[50vh] md:min-h-screen cursor-pointer">
          <div className="absolute inset-0 bg-slate-950" />
          <img src="/ChurchBG.webp" alt="" role="presentation"
            className={`absolute inset-0 h-full w-full object-cover blur-[1px] transition-all duration-700 scale-105 ${activePanel === 'mn' ? 'opacity-35' : 'opacity-0 md:group-hover:opacity-35'}`} />
          <div className="absolute inset-0 bg-gradient-to-bl from-amber-950/30 via-transparent to-transparent opacity-60" />
          <div className="absolute inset-0 bg-amber-500/0 group-hover:bg-amber-500/[0.03] transition-colors duration-500" />

          <div className="relative z-10 flex flex-col items-center text-center px-8 pt-10 pb-14 md:py-24 max-w-sm md:max-w-md">
            <div className="mb-6 h-14 w-14 md:h-16 md:w-16 rounded-2xl bg-amber-500/[0.09] border border-amber-500/20 grid place-items-center text-amber-400 group-hover:bg-amber-500/[0.16] group-hover:border-amber-500/35 group-hover:scale-110 transition-all duration-400">
              <IconCross />
            </div>
            <p className="text-[10px] font-bold tracking-[0.32em] text-amber-400/60 uppercase mb-3">For Churches & Ministries</p>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white leading-[1.08] mb-4 group-hover:-translate-y-1 transition-transform duration-400">
              Build My<br />Church Website
            </h2>
            <p className="text-slate-400 text-sm md:text-[15px] leading-relaxed mb-7">
              Church websites with online giving, live streaming,
              and everything your congregation needs.
            </p>
            <span className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-6 py-3 text-sm font-semibold text-white group-hover:bg-amber-600 group-hover:shadow-xl group-hover:shadow-amber-950/60 transition-all duration-300">
              Get Started
              <span className="group-hover:translate-x-0.5 transition-transform duration-300 inline-flex">
                <IconArrow />
              </span>
            </span>
          </div>
        </Link>
      </div>

      {/* Bottom caption — desktop only */}
      <div className="absolute bottom-5 left-0 right-0 hidden md:flex justify-center pointer-events-none">
        <p className="text-[11px] text-slate-700 font-medium tracking-widest uppercase">
          Professional websites · Completely hands free
        </p>
      </div>
    </div>
  );
}
